/* eslint-disable react/jsx-indent */
import { FC, useMemo } from 'react';
import i18n from 'i18next';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import Button from 'modules/_shared/components/Buttons';
import { HoldingClass } from 'types';
import { useEquityMarket } from '../contexts/EquityMarketContext';
import {
  TradingOffer,
  TradingOffersSummary,
  TradingOfferType,
} from '../../../types/TradingOffer';
import { isOfferActive } from '../utils/getOfferStatus';

type MarketSummaryCardProps = {
  type: TradingOfferType;
  title: string;
  summary: TradingOffersSummary;
  avgPrice: number;
  offers: TradingOffer[];
  actionLabel: string;
  actionVariant: 'success' | 'primary';
  viewAllLabel: string;
  onViewOffer: (offerId: string) => void; // eslint-disable-line no-unused-vars
  onViewAll: () => void;
};

const getPartnerName = (offer: TradingOffer): string => {
  if (offer.isSellerNameHidden && offer.type === 'SELL') {
    return i18n.t('HiddenName');
  }
  if (typeof offer.partner === 'string') return offer.partner;
  return offer.partner?.name || '';
};

const MarketSummaryCard: FC<MarketSummaryCardProps> = ({
  type,
  title,
  summary,
  avgPrice,
  offers,
  actionLabel,
  actionVariant,
  viewAllLabel,
  onViewOffer,
  onViewAll,
}) => {
  const { t } = useTranslate();

  const mainStyle = { SELL: 'success', BUY: 'primary' };

  const activeLabel =
    offers.length === 1
      ? t('OfferActive').toLowerCase()
      : t('OfferActives').toLowerCase();

  return (
    <div
      className={`card card-bordered h-100 border-top-${mainStyle[type]}`}
      style={{ borderTopWidth: `3px` }}
    >
      <div className="card-inner h-100 d-flex flex-column">
        <div className="nk-block-head-sm">
          <div className="nk-block-between flex-wrap">
            <h5 className="nk-block-title mb-0">{title}</h5>
            <span className={`badge badge-dim badge-${mainStyle[type]}`}>
              {offers.length} {activeLabel}
            </span>
          </div>
          <p className="text-soft mb-3 mt-1">{t('SortedByPriceLowToHigh')}</p>
        </div>

        <div className="d-flex flex-nowrap justify-content-between mb-3 pb-3">
          <div className="text-center">
            <div className="overline-title-alt sub-text">
              {t('TotalShares')}
            </div>
            <div className="lead-text amount">
              {formatNumber(summary.sharesCount)}
            </div>
          </div>
          <div className="text-center">
            <div className="overline-title-alt sub-text">{t('AvgPrice')}</div>
            <div className="lead-text">
              <span className="amount">{formatCurrencyDecimals(avgPrice)}</span>
              <span className="text-soft">/{t('Share').toLowerCase()}</span>
            </div>
          </div>
          <div className="text-center">
            <div className="overline-title-alt sub-text">
              {t('TotalVolume')}
            </div>
            <div
              className={`lead-text amount text-${
                mainStyle[type as TradingOfferType]
              }`}
            >
              {formatCurrencyDecimals(summary.totalAmount)}
            </div>
          </div>
        </div>

        <div className="mb-0 flex-grow-1">
          {[...offers]
            .sort((a, b) => a.sharePrice - b.sharePrice)
            .slice(0, 3)
            .map((offer, index) => (
              <div
                key={offer._id}
                className={`d-flex justify-content-between align-items-center py-2 border-bottom border-light ${
                  index === 0 ? 'border-top' : ''
                }`}
              >
                <div className="flex-grow-1">
                  <div className="fw-medium">
                    {formatNumber(offer.sharesCount.total)} acc.{' '}
                    {(offer.shareClass as HoldingClass)?.name || '-'} |{' '}
                    {formatCurrencyDecimals(offer.sharePrice)}/acc |{' '}
                    {getPartnerName(offer)}{' '}
                  </div>
                  <div className="text-soft fs-12px">
                    Total:{' '}
                    {formatCurrencyDecimals(
                      offer.sharesCount.total * offer.sharePrice
                    )}
                  </div>
                </div>
                {/* <span
                  className={`badge badge-dim badge-${
                    mainStyle[type as TradingOfferType]
                  } ml-2`}
                >
                  {t('Active')}
                </span> */}
                <div className="ml-3">
                  <button
                    type="button"
                    className={`btn btn-sm btn-${actionVariant}`}
                    onClick={() => onViewOffer(offer._id)}
                  >
                    {actionLabel}
                  </button>
                </div>
              </div>
            ))}
        </div>

        <Button
          variant="link"
          onClick={onViewAll}
          className="link link-primary mt-auto pt-3 btn btn-link p-0 text-start"
          disabled={offers?.length === 0}
        >
          {viewAllLabel} <em className="icon ni ni-arrow-long-right ml-1" />
        </Button>
      </div>
    </div>
  );
};

const EquityMarketSummaryCards: FC = () => {
  const { t } = useTranslate();
  const { data, actions } = useEquityMarket();

  const avgSellPrice = useMemo(() => {
    if (data.sellOffersSummary.sharesCount === 0) return 0;
    return (
      data.sellOffersSummary.totalAmount / data.sellOffersSummary.sharesCount
    );
  }, [data.sellOffersSummary]);

  const avgBuyPrice = useMemo(() => {
    if (data.buyOffersSummary.sharesCount === 0) return 0;
    return (
      data.buyOffersSummary.totalAmount / data.buyOffersSummary.sharesCount
    );
  }, [data.buyOffersSummary]);

  const filterActiveOffers = (offers: TradingOffer[]) =>
    offers
      .filter((offer) => isOfferActive(offer))
      .sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );

  return (
    <div className="nk-block nk-block-spacing">
      <div className="row gy-gs">
        {/* Sell Offers Summary */}
        <div className="col-12 col-lg-6">
          <MarketSummaryCard
            type={TradingOfferType.SELL}
            title={t('SellOffers')}
            summary={data.sellOffersSummary}
            avgPrice={avgSellPrice}
            offers={filterActiveOffers(data.sellOffers)}
            actionLabel={t('Buy')}
            actionVariant="success"
            viewAllLabel={t('ViewAllSellOffers')}
            onViewOffer={actions.handleViewOfferDetail}
            onViewAll={actions.handleViewSellOffers}
          />
        </div>

        {/* Buy Offers Summary */}
        <div className="col-12 col-lg-6">
          <MarketSummaryCard
            type={TradingOfferType.BUY}
            title={t('BuyOffers')}
            summary={data.buyOffersSummary}
            avgPrice={avgBuyPrice}
            offers={filterActiveOffers(data.buyOffers)}
            actionLabel={t('Sell')}
            actionVariant="primary"
            viewAllLabel={t('ViewAllBuyOffers')}
            onViewOffer={actions.handleViewOfferDetail}
            onViewAll={actions.handleViewBuyOffers}
          />
        </div>
      </div>
    </div>
  );
};

export default EquityMarketSummaryCards;
